import { StandardEvents } from '@shopify/events';

class AuraPdpRecommendations extends HTMLElement {
  #observer;
  #controller;
  #version = 0;
  #pool = [];
  #cartIds = new Set();

  connectedCallback() {
    // Ignore recommendation endpoint payload copies.
    if (this.querySelector('[data-aura-pdp-payload]')) return;

    this.#setCartIds((this.dataset.cartProductIds || '').split(','));

    const sentinel =
      this.querySelector('[data-aura-pdp-sentinel]') || this;

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;

        this.#observer.disconnect();
        this.dataset.loaded = 'true';
        this.loadCandidates();
      },
      {
        rootMargin: '0px 0px 500px 0px',
      }
    );

    this.#observer.observe(sentinel);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#controller?.abort();
    this.#version += 1;
  }

  #setCartIds(ids) {
    this.#cartIds = new Set(
      (ids || [])
        .map((id) => String(id || '').trim())
        .filter(Boolean)
    );

    this.dataset.cartProductIds =
      [...this.#cartIds].join(',');
  }

  syncCartItems(items) {
    if (!Array.isArray(items)) return;

    this.#setCartIds(
      items.map((item) => item.product_id)
    );

    this.#render();
  }

  optimisticAdd(productId) {
    productId = String(productId || '');
    if (!productId) return;

    this.#cartIds.add(productId);

    this.dataset.cartProductIds =
      [...this.#cartIds].join(',');

    this.#render();
  }

  undoOptimisticAdd(productId) {
    productId = String(productId || '');
    if (!productId) return;

    this.#cartIds.delete(productId);

    this.dataset.cartProductIds =
      [...this.#cartIds].join(',');

    this.#render();
  }

  /*
    PDP recommendation source:

    1. Related products for CURRENT PDP product
    2. Complementary products for CURRENT PDP product

    Cart contents never become recommendation anchors.
  */
  async loadCandidates() {
    const productId =
      String(this.dataset.productId || '');

    if (!productId) return;

    const version = ++this.#version;

    this.#controller?.abort();
    this.#controller = new AbortController();

    this.dataset.state = 'loading';
    this.setAttribute('aria-busy', 'true');

    try {
      const [related, complementary] =
        await Promise.all([
          this.#fetchCards(
            productId,
            'related',
            this.#controller.signal
          ),
          this.#fetchCards(
            productId,
            'complementary',
            this.#controller.signal
          ),
        ]);

      if (version !== this.#version) return;

      const seen = new Set();

      this.#pool = [
        ...related,
        ...complementary,
      ].filter((card) => {
        if (
          !card.productId ||
          seen.has(card.productId)
        ) {
          return false;
        }

        seen.add(card.productId);
        return true;
      });

      this.#render();
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn(
          '[Aura PDP Recommendations]',
          error
        );

        this.dataset.state = 'error';
      }
    } finally {
      if (version === this.#version) {
        this.removeAttribute('aria-busy');
      }
    }
  }

  async #fetchCards(productId, intent, signal) {
    const params = new URLSearchParams({
      product_id: productId,
      limit: '10',
      section_id: String(this.dataset.sectionId),
      intent,
    });

    try {
      const response = await fetch(
        `${this.dataset.url}?${params}`,
        {
          signal,
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        }
      );

      if (!response.ok) return [];

      const doc =
        new DOMParser().parseFromString(
          await response.text(),
          'text/html'
        );

      const payload =
        doc.querySelector(
          '[data-aura-pdp-payload]'
        );

      if (!payload) return [];

      return [
        ...payload.querySelectorAll(
          '.aura-pdp-recommendations__item'
        ),
      ].map((card) => ({
        productId:
          String(card.dataset.productId || ''),
        html: card.outerHTML,
      }));
    } catch (error) {
      if (error?.name === 'AbortError') {
        throw error;
      }

      console.warn(
        `[Aura PDP Recommendations] ${intent}:`,
        error
      );

      return [];
    }
  }

  #render() {
    const results =
      this.querySelector(
        '[data-aura-pdp-results]'
      );

    const content =
      this.querySelector(
        '[data-aura-pdp-content]'
      );

    if (!results || !content) return;

    const currentId =
      String(this.dataset.productId || '');

    const limit = Math.max(
      1,
      parseInt(this.dataset.limit || '4', 10) || 4
    );

    const selected = this.#pool
      .filter(
        (card) =>
          card.productId !== currentId &&
          !this.#cartIds.has(card.productId)
      )
      .slice(0, limit);

    results.innerHTML =
      selected
        .map((card) => card.html)
        .join('');

    content.hidden =
      selected.length === 0;

    this.dataset.state =
      selected.length ? 'ready' : 'empty';
  }
}

if (
  !customElements.get(
    'aura-pdp-recommendations'
  )
) {
  customElements.define(
    'aura-pdp-recommendations',
    AuraPdpRecommendations
  );
}

function getAddedProductId(event) {
  if (
    event.action !== 'add' ||
    !(event.target instanceof Element)
  ) {
    return '';
  }

  return String(
    event.target
      .closest('product-form-component')
      ?.dataset.productId || ''
  );
}

document.addEventListener(
  StandardEvents.cartLinesUpdate,
  (event) => {
    const addedId =
      getAddedProductId(event);

    const elements = () =>
      document.querySelectorAll(
        'aura-pdp-recommendations'
      );

    /*
      ADD:
      Hide the product immediately.
      No recommendation network request.
    */
    if (addedId) {
      elements().forEach((element) =>
        element.optimisticAdd?.(addedId)
      );
    }

    const cartUpdate =
      event.promise?.then
        ? event.promise
        : Promise.resolve();

    cartUpdate
      .then((result) => {
        const items =
          result?.detail?.items ||
          event.detail?.items;

        elements().forEach((element) => {
          if (Array.isArray(items)) {
            element.syncCartItems?.(items);
          }

          /*
            REMOVE only:
            refresh the same PDP product's pool because
            a removed product may become eligible again.
          */
          if (event.action === 'remove') {
            element.loadCandidates?.();
          }
        });
      })
      .catch((error) => {
        if (addedId) {
          elements().forEach((element) =>
            element.undoOptimisticAdd?.(addedId)
          );
        }

        if (error?.name !== 'AbortError') {
          console.warn(
            '[Aura PDP Recommendations] Cart update rejected:',
            error
          );
        }
      });
  }
);